import { useEffect, useState } from "react";
import { HelpCircle, X } from "lucide-react";

/** What the numbers actually mean. The monitoring tool's own definitions, kept out of the
 *  way behind a link — the same wording is repeated in the emails and PDFs so
 *  a manager reading a report offline gets the identical explanation. */
export const METRIC_LEGEND = [
  ["Session time", "The total time a user's computer has been on — including time it was locked or the screensaver was running."],
  ["Work time", "The total time the user has been logged into their machine with the screen unlocked."],
  ["Idle time", "Time the user was inactive on their machine (not typing or moving the mouse). Idle only starts counting after a 10-minute threshold, and only the time beyond it is counted."],
  ["Productive time", "Time spent on applications and websites classified as Productive."],
  ["Unproductive time", "Time spent on applications and websites classified as Unproductive."],
  ["Activity (input rate)", "The monitoring tool's own dashboard percentage (keyboard and mouse input rate). It is deliberately NOT shown in Conduit: the reporting API returns only total and idle seconds per app, never the input rate, so any figure here would not match the tool's own screen."],
  ["Unclassified time", "Tracked time on an app or site nobody has classified yet. Classify the long tail on the Applications tab to shrink it."],
];

export function MetricLegend({ className = "" }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="prod-legend-open"
        className={`inline-flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 transition-colors hover:text-primary ${className}`}>
        <HelpCircle className="h-3.5 w-3.5" /> What do these numbers mean?
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setOpen(false)} data-testid="prod-legend-overlay">
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white dark:bg-slate-900 p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()} data-testid="prod-legend-panel">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Where these numbers come from</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">The monitoring tool&apos;s definitions, as recorded on the agent.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} data-testid="prod-legend-close"
                className="text-slate-400 dark:text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <dl className="space-y-3">
              {METRIC_LEGEND.map(([term, def]) => (
                <div key={term}>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">{term}</dt>
                  <dd className="mt-0.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{def}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3 text-xs text-slate-400 dark:text-slate-400">
              One caveat specific to this report: per-app totals overlap (background apps and sites
              inside a browser), so figures are measured against each person&apos;s real time at the
              machine that day rather than summed raw. Idle allowance credits on-call waiting back
              as productive.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
